/* eslint-disable prefer-arrow-callback, no-plusplus, no-continue, no-restricted-syntax */
import {
  Snackbar,
  Alert,
  TextField,
  Box,
  InputAdornment,
  Icon,
  Typography,
} from "@mui/material";
import PropTypes from "prop-types";
import { useState, useEffect } from "react";
import MDBox from "theme/components/MDBox";
import MDInput from "theme/components/MDInput";
import MDTypography from "theme/components/MDTypography";

function PaymentField({ displayName, fieldRef, options }) {
  const [fieldError, showFieldError] = useState(false);
  const [errMsg, setErrMsg] = useState("Invalid payment configuration.");
  const [amount, setAmount] = useState(0);
  const [formattedAmount, setFormattedAmount] = useState("");

  const mode = options?.mode || "fixed";

  const handleInputChange = (e) => {
    const inputVal = e.target.value;
    const numberVal = inputVal.replace(/,/g, "");
    if (numberVal.length === 0) {
      setFormattedAmount("");
      return;
    }

    if (numberVal.endsWith(".")) {
      setFormattedAmount(numberVal);
      return;
    }

    const isnum = /^[0-9]\d*(\.\d+)?$/.test(numberVal);
    if (!isnum && !numberVal.endsWith(".")) {
      alert("No letters allowed.");
      setAmount(null);
      return;
    }

    // const x = parseFloat(numberVal);
    const fNum = parseFloat(numberVal, 10).toLocaleString("en", {
      // minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    setAmount(numberVal);
    setFormattedAmount(fNum);
  };

  function formatAmount(val) {
    return Number(val || 0).toLocaleString("en", {
      minimumFractionDigits: 2,
    });
  }

  function computeAmount() {
    if (mode === "fixed") {
      const amt = Number(options?.amount || 0);
      setAmount(amt);
      setFormattedAmount(formatAmount(amt / 100));
      return;
    }

    if (mode === "computed") {
      const fields = options?.source_fields || [];
      let total = 0;

      for (const id of fields) {
        const el = document.getElementById(`dff_${id}`);
        if (!el) continue;

        total += Number(el.value || 0);
      }

      setAmount(total);
      setFormattedAmount(formatAmount(total));
    }
  }

  function initListener() {
    if (mode !== "computed") return;

    const fields = options?.source_fields || [];

    for (const id of fields) {
      const el = document.getElementById(`dff_${id}`);
      if (!el) continue;

      el.addEventListener("change", function () {
        computeAmount();
      });
    }
  }

  useEffect(() => {
    if (mode !== "open") {
      computeAmount();
      initListener();
    }
  }, []);

  useEffect(() => {
    const watchField = document.getElementById(`dff_${fieldRef}`);
    if (watchField) {
      watchField.dispatchEvent(new Event("change"));
    }
  }, [amount]);

  return (
    <Box mt={3}>
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        autoHideDuration={5000}
        onClose={() => showFieldError(false)}
        open={fieldError}
      >
        <Alert severity="error" sx={{ width: "100%" }}>
          {errMsg}
        </Alert>
      </Snackbar>

      <MDBox ml={0.5}>
        <MDTypography variant="h6" fontSize={14} fontWeight="light">
          {displayName}
        </MDTypography>
      </MDBox>
      {mode === "open" && (
        <MDInput
          type="text"
          fullWidth
          variant="outlined"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Typography fontSize={18}>{options.currency}</Typography>
              </InputAdornment>
            ),
          }}
          InputLabelProps={{
            sx: { whiteSpace: "break-spaces" },
          }}
          value={formattedAmount}
          onChange={handleInputChange}
        />
      )}

      {mode !== "open" && (
        <MDInput
          mt={3}
          type="text"
          fullWidth
          variant="outlined"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Typography fontSize={18}>{options.currency}</Typography>
              </InputAdornment>
            ),
          }}
          InputLabelProps={{
            sx: { whiteSpace: "break-spaces" },
          }}
          value={formattedAmount}
          inputProps={{ readOnly: true }}
          disabled
        />
      )}

      <input
        id={`dff_${fieldRef}`}
        type="hidden"
        name={fieldRef}
        value={Math.round(Number(amount || 0) * 100)}
      />
    </Box>
  );
}

// props
PaymentField.propTypes = {
  fieldRef: PropTypes.string.isRequired,
  displayName: PropTypes.string.isRequired,
  options: PropTypes.object,
};

PaymentField.defaultProps = {
  options: null,
};

export default PaymentField;
